import { CommonModule } from "@angular/common";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostBinding,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges
} from "@angular/core";
import {
    FormBuilder,
    FormGroup,
    Validators,
    ValidatorFn,
    AbstractControl,
    ReactiveFormsModule,
    FormsModule
} from "@angular/forms";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { NzModalService } from "ng-zorro-antd/modal";
import { Subject, Subscription, takeUntil } from "rxjs";
import { animate, style, transition, trigger } from "@angular/animations";
import { NzButtonModule } from "ng-zorro-antd/button";
import { NzFormModule } from "ng-zorro-antd/form";
import { NzInputModule } from "ng-zorro-antd/input";
import { NzIconModule } from "ng-zorro-antd/icon";
import { NzModalModule } from "ng-zorro-antd/modal";
import { NzSpinModule } from "ng-zorro-antd/spin";
import { NzPaginationModule } from "ng-zorro-antd/pagination";
import { NzSelectModule } from "ng-zorro-antd/select";
import { NavigationMenuSelection } from "../../../../interfaces/navigation_menu.interface";
import {
    CreateQuestionResponseLinkPayload,
    QuestionResponseLinkDto,
    QuestionResponseLinksService
} from "../../../../services/question_response_links.service";
import { QuestionResponsesService } from "../../../../services/question_responses.service";
import { ProductLicenseSummary, ProductLicensesService } from "../../../../services/product_licenses.service";
import { LoginService } from "../../../../services/login.service";
import { FormNavigationDispatcherService } from "../../../../services/form_navigation_dispatcher.service";

interface SupportingDocumentationLink {
    id: number;
    userId: number | null;
    displayName: string;
    url: string;
    type: string;
    createdAt: string;
    updatedAt: string | null;
    questionReference: string | null;
    createdByDisplayName: string | null;
}

const SECTION_PATTERN = /^[A-Za-z0-9_]+$/;
const TARGET_PATTERN = /^[A-Za-z0-9 _-]+$/;

@Component({
    selector: "td[appSaqSupportingDocumentationFooter]",
    templateUrl: "./supporting_documentation.component.html",
    styleUrls: ["./supporting_documentation.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        NzButtonModule,
        NzFormModule,
        NzInputModule,
        NzIconModule,
        NzModalModule,
        NzSpinModule,
        NzSelectModule,
        NzPaginationModule
    ],
    animations: [
        trigger("rowAnimation", [
            transition(":enter", [
                style({ opacity: 0, transform: "translateY(-6px)" }),
                animate("200ms ease-out", style({ opacity: 1, transform: "translateY(0)" }))
            ]),
            transition(":leave", [
                style({ height: "*", opacity: 1, transform: "translateY(0)" }),
                animate(
                    "180ms ease-in",
                    style({
                        height: 0,
                        opacity: 0,
                        transform: "translateY(-8px)",
                        marginTop: 0,
                        marginBottom: 0,
                        paddingTop: 0,
                        paddingBottom: 0
                    })
                )
            ])
        ])
    ]
})
export class SupportingDocumentationFooterComponent implements OnChanges, OnDestroy {
    private static nextInstanceId = 0;
    private static readonly URL_PROTOCOL_PATTERN = /^[a-zA-Z][a-zA-Z0-9+.-]*:/;
    private static readonly CUSTOM_LINK_TYPE = "Other";
    private static readonly CUSTOM_LINK_TYPE_MAX_LENGTH = 20;
    private static readonly LINK_TYPE_OPTIONS = [
        "Documentation",
        "Image / Diagram",
        "Interview notes",
        "Certificate",
        SupportingDocumentationFooterComponent.CUSTOM_LINK_TYPE
    ] as const;
    private static readonly DEFAULT_LINK_TYPE = SupportingDocumentationFooterComponent.LINK_TYPE_OPTIONS[0];

    @Input()
    sectionId = "";

    @Input()
    sectionKey = "";

    @Input()
    readOnlyUser = false;

    @Input()
    aggregateByLicense = false;

    @Input()
    productLicenseId: number | null = null;

    @HostBinding("class")
    readonly hostClass = "supporting-documentation";

    readonly componentId = ++SupportingDocumentationFooterComponent.nextInstanceId;

    protected readonly linkForm: FormGroup;
    protected readonly linkTypeOptions = [...SupportingDocumentationFooterComponent.LINK_TYPE_OPTIONS];
    protected readonly customTypeMaxLength = SupportingDocumentationFooterComponent.CUSTOM_LINK_TYPE_MAX_LENGTH;
    protected links: SupportingDocumentationLink[] = [];
    protected pageIndex = 1;
    protected readonly pageSize = 10;
    protected total = 0;
    private pageRequest: Subscription | null = null;
    private searchTimer: ReturnType<typeof setTimeout> | null = null;

    protected filteredLinks: SupportingDocumentationLink[] = [];
    protected authorOptions: string[] = [];
    protected areFiltersVisible = false;
    protected linkSearchText = "";
    protected selectedAuthorNames: string[] = [];
    protected isLoading = false;
    protected isExpanded = false;
    protected isFormVisible = false;
    protected isSubmitting = false;
    protected hasSelection = false;
    protected canViewSupportingDocumentation = false;
    protected readonly deletingLinkIds = new Set<number>();
    protected isCustomTypeSelected = false;
    protected editingLinkId: number | null = null;
    protected isSavingEdit = false;
    private editingLicenseId: number | null = null;

    private currentSelection: NavigationMenuSelection | null = null;
    private lastSelectionKey: string | null = null;
    private lastContextLicenseId: number | null = null;
    private lastAggregateByLicense = false;
    private loadSequence = 0;
    private readonly destroy$ = new Subject<void>();
    private licenseAccessSubscription: Subscription | null = null;

    constructor(
        private readonly questionResponseLinks: QuestionResponseLinksService,
        private readonly questionResponsesService: QuestionResponsesService,
        private readonly productLicensesService: ProductLicensesService,
        private readonly loginService: LoginService,
        private readonly notification: NzNotificationService,
        private readonly modal: NzModalService,
        private readonly navigationDispatcher: FormNavigationDispatcherService,
        private readonly fb: FormBuilder,
        private readonly cdr: ChangeDetectorRef
    ) {
        this.linkForm = this.fb.group({
            type: [
                SupportingDocumentationFooterComponent.DEFAULT_LINK_TYPE,
                [Validators.required, this.createLinkTypeValidator()]
            ],
            customType: [""],
            displayName: [
                "",
                [Validators.required, Validators.maxLength(255), this.createWhitespaceValidator()]
            ],
            url: [
                "",
                [
                    Validators.required,
                    Validators.maxLength(2048),
                    this.createWhitespaceValidator(),
                    this.createUrlValidator()
                ]
            ]
        });

        const typeControl = this.linkForm.controls["type"];
        typeControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
            this.updateCustomTypeState(value);
        });
        this.updateCustomTypeState(typeControl.value, { markForCheck: false });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["sectionId"] || changes["sectionKey"] || changes["aggregateByLicense"] ||
            (changes["readOnlyUser"] && this.readOnlyUser)) {
            this.isExpanded = false;
        }
        if (changes["sectionId"] || changes["sectionKey"] || changes["readOnlyUser"] || changes["aggregateByLicense"] || changes["productLicenseId"]) {
            this.updateSelection(this.sectionId, this.sectionKey);
        }
    }

    ngOnDestroy(): void {
        this.pageRequest?.unsubscribe();
        if (this.searchTimer) clearTimeout(this.searchTimer);
        this.destroy$.next();
        this.destroy$.complete();
        this.licenseAccessSubscription?.unsubscribe();
    }

    @HostListener("click")
    protected expandIfCollapsed(): void {
        if (!this.isExpanded) {
            this.isExpanded = true;
            this.markForCheck();
        }
    }

    protected toggleExpanded(): void {
        this.isExpanded = !this.isExpanded;
        this.markForCheck();
    }

    protected trackById(_index: number, item: SupportingDocumentationLink): number {
        return item.id;
    }

    protected formatLinkHref(value: string | null | undefined): string | null {
        return this.withDefaultProtocol(value);
    }

    protected showFilters(): void {
        this.areFiltersVisible = true;
        this.markForCheck();
    }

    protected onLinkSearchChange(value: string): void {
        this.linkSearchText = typeof value === "string" ? value.slice(0, 256) : "";
        this.queuePageSearch();
        this.markForCheck();
    }

    protected onAuthorFilterChange(values: string[]): void {
        this.selectedAuthorNames = Array.isArray(values) ? values : [];
        this.pageIndex = 1;
        this.loadLinks(this.aggregateByLicense ? null : this.currentSelection);
        this.markForCheck();
    }

    protected onAddLinkClicked(): void {
        if (this.readOnlyUser || this.editingLinkId !== null) {
            return;
        }

        if (!this.hasSelection && !this.aggregateByLicense) {
            this.notification.warning(
                "Select a requirement",
                "Choose a requirement before adding supporting documentation."
            );
            return;
        }

        this.isExpanded = true;
        this.isFormVisible = true;
        this.isSubmitting = false;
        this.resetFormControls();
    }

    protected onCancelForm(): void {
        this.isFormVisible = false;
        this.isSubmitting = false;
        this.resetFormControls();
    }

    protected onSubmitLink(): void {
        if (this.isSubmitting || this.readOnlyUser) {
            return;
        }

        const selection = this.currentSelection;
        const licenseId = this.getCurrentLicenseId();
        const reference = this.toQuestionReference(selection);
        const aggregateByLicense = this.aggregateByLicense;
        if (licenseId === null) return;
        if (!selection && !this.aggregateByLicense) {
            this.notification.error(
                "Add link failed",
                "A valid question context is not available."
            );
            return;
        }

        this.linkForm.markAllAsTouched();
        this.linkForm.updateValueAndValidity();

        if (this.linkForm.invalid) {
            this.markForCheck();
            return;
        }

        const payload: CreateQuestionResponseLinkPayload = {
            type: this.determineSubmissionType(),
            displayName: String(this.linkForm.value.displayName ?? "").trim(),
            url: this.normalizeSubmissionUrl(String(this.linkForm.value.url ?? ""))
        };

        this.isSubmitting = true;
        this.markForCheck();

        this.questionResponseLinks
            .createLink(aggregateByLicense ? null : selection, payload, licenseId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    if (!this.isCurrentLinkContext(licenseId, reference, aggregateByLicense)) return;
                    this.pageIndex = 1;
                    this.loadLinks(this.aggregateByLicense ? null : this.currentSelection);
                    this.notification.success(
                        "Supporting evidence added",
                        "The supporting documentation link has been saved."
                    );
                    this.isSubmitting = false;
                    this.isFormVisible = false;
                    this.resetFormControls();
                },
                error: (error) => {
                    if (!this.isCurrentLinkContext(licenseId, reference, aggregateByLicense)) return;
                    this.isSubmitting = false;
                    const message =
                        error?.error?.message ||
                        error?.message ||
                        "Unable to add supporting documentation link.";
                    this.notification.error("Add link failed", message);
                    this.markForCheck();
                }
            });
    }

    protected onDeleteLink(link: SupportingDocumentationLink): void {
        if (!this.canDeleteLink(link) || this.editingLinkId !== null || this.deletingLinkIds.has(link.id)) {
            return;
        }

        this.modal.confirm({
            nzTitle: "Delete supporting evidence?",
            nzContent: `Are you sure you want to delete \"${link.displayName}\"?`,
            nzOkText: "Confirm",
            nzCancelText: "Cancel",
            nzOkDanger: true,
            nzCentered: true,
            nzOnOk: () => {
                this.performDelete(link);
            }
        });
    }

    private performDelete(link: SupportingDocumentationLink): void {
        const selection = this.currentSelection;
        const licenseId = this.getCurrentLicenseId();
        const reference = this.toQuestionReference(selection);
        const aggregateByLicense = this.aggregateByLicense;
        if (licenseId === null || (!selection && !aggregateByLicense) || !this.canDeleteLink(link) ||
            !this.links.some((item) => item.id === link.id)) {
            this.notification.error(
                "Delete failed",
                "A valid question context is not available."
            );
            return;
        }

        this.deletingLinkIds.add(link.id);
        this.markForCheck();

        this.questionResponseLinks
            .deleteLink(link.id, aggregateByLicense ? null : selection, licenseId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    if (!this.isCurrentLinkContext(licenseId, reference, aggregateByLicense)) return;
                    this.loadLinks(this.aggregateByLicense ? null : this.currentSelection);
                    this.deletingLinkIds.delete(link.id);
                    this.notification.success(
                        "Supporting evidence removed",
                        "The supporting documentation link has been deleted."
                    );
                    this.markForCheck();
                },
                error: (error) => {
                    if (!this.isCurrentLinkContext(licenseId, reference, aggregateByLicense)) return;
                    this.deletingLinkIds.delete(link.id);
                    const message =
                        error?.error?.message ||
                        error?.message ||
                        "Unable to delete the supporting documentation link.";
                    this.notification.error("Delete failed", message);
                    this.markForCheck();
                }
            });
    }

    protected canDeleteLink(link: SupportingDocumentationLink): boolean {
        return !this.readOnlyUser && this.canViewSupportingDocumentation &&
            (this.loginService.hasAdminPrivileges() || this.isLinkAuthor(link));
    }

    protected canEditLink(link: SupportingDocumentationLink): boolean {
        return !this.readOnlyUser && this.canViewSupportingDocumentation && this.isLinkAuthor(link);
    }

    private isLinkAuthor(link: SupportingDocumentationLink): boolean {
        const currentUserId = this.normalizeUserId(this.loginService.getCurrentUserId());
        return currentUserId !== null && link.userId === currentUserId;
    }

    protected onEditLink(link: SupportingDocumentationLink): void {
        if (!this.canEditLink(link) || this.isLoading || this.isSubmitting || this.isFormVisible ||
            this.editingLinkId !== null || this.deletingLinkIds.has(link.id)) return;
        this.editingLinkId = link.id;
        this.editingLicenseId = this.getCurrentLicenseId();
        const knownType = this.linkTypeOptions.find((option) => option.toLowerCase() === link.type.toLowerCase());
        this.linkForm.reset({
            type: knownType ?? "Other",
            customType: knownType ? "" : link.type,
            displayName: link.displayName,
            url: link.url
        });
        this.markForCheck();
    }

    protected onCancelEdit(): void {
        if (this.isSavingEdit) return;
        this.clearEditState();
    }

    private clearEditState(): void {
        this.editingLinkId = null;
        this.editingLicenseId = null;
        this.isSavingEdit = false;
        this.resetFormControls();
    }

    protected onSaveEdit(link: SupportingDocumentationLink): void {
        if (this.editingLinkId !== link.id || !this.canEditLink(link) || this.isSavingEdit || this.isLoading) return;
        this.linkForm.markAllAsTouched();
        this.linkForm.updateValueAndValidity();
        if (this.linkForm.invalid) { this.markForCheck(); return; }
        const payload: CreateQuestionResponseLinkPayload = {
            type: this.determineSubmissionType(),
            displayName: String(this.linkForm.value.displayName ?? "").trim(),
            url: this.normalizeSubmissionUrl(String(this.linkForm.value.url ?? ""))
        };
        if (payload.type === link.type && payload.displayName === link.displayName && payload.url === link.url) {
            this.clearEditState();
            return;
        }
        this.modal.confirm({
            nzTitle: "Save changes to this supporting documentation?",
            nzOkText: "Save", nzCancelText: "Cancel", nzCentered: true,
            nzOnOk: () => this.performEdit(link, payload)
        });
    }

    private performEdit(link: SupportingDocumentationLink, payload: CreateQuestionResponseLinkPayload): void {
        const licenseId = this.getCurrentLicenseId();
        const selection = this.currentSelection;
        if (this.editingLinkId !== link.id || !this.canEditLink(link) || this.isSavingEdit ||
            licenseId === null || licenseId !== this.editingLicenseId ||
            (!selection && !this.aggregateByLicense) || !this.links.some((item) => item.id === link.id)) return;
        this.isSavingEdit = true;
        this.markForCheck();
        this.questionResponseLinks.updateLink(link.id, this.aggregateByLicense ? null : selection, payload, licenseId)
            .pipe(takeUntil(this.destroy$)).subscribe({
                next: (response) => {
                    if (this.editingLinkId !== link.id || this.editingLicenseId !== licenseId) return;
                    this.links = this.links.map((item) => item.id === link.id ? this.normalizeLink(response) : item);
                    this.rebuildFilteredLinks();
                    this.clearEditState();
                    this.loadLinks(this.aggregateByLicense ? null : this.currentSelection);
                    this.notification.success("Supporting evidence updated", "The supporting documentation link has been saved.");
                },
                error: (error) => {
                    this.isSavingEdit = false;
                    this.notification.error("Edit link failed", error?.error?.message || error?.message || "Unable to edit the supporting documentation link.");
                    this.markForCheck();
                }
            });
    }

    private resetFormControls(): void {
        this.linkForm.reset({
            type: SupportingDocumentationFooterComponent.DEFAULT_LINK_TYPE,
            customType: "",
            displayName: "",
            url: ""
        });
        this.linkForm.markAsPristine();
        this.linkForm.markAsUntouched();
        this.updateCustomTypeState(this.linkForm.controls["type"].value, { markForCheck: false });
        this.markForCheck();
    }

    private updateSelection(sectionId: string, sectionKey: string): void {
        const normalized = this.normalizeSelection(sectionId, sectionKey);
        const reference = normalized ? this.toQuestionReference(normalized) : null;
        const licenseId = this.getCurrentLicenseId();
        const contextChanged = reference !== this.lastSelectionKey || licenseId !== this.lastContextLicenseId ||
            this.aggregateByLicense !== this.lastAggregateByLicense;
        if (contextChanged) {
            ++this.loadSequence;
            this.licenseAccessSubscription?.unsubscribe();
            this.licenseAccessSubscription = null;
            if (this.editingLinkId !== null) this.clearEditState();
            this.resetFilters();
            this.links = [];
            this.rebuildFilteredLinks();
            this.canViewSupportingDocumentation = false;
        }
        this.lastContextLicenseId = licenseId;
        this.lastAggregateByLicense = this.aggregateByLicense;
        if (this.readOnlyUser) {
            this.links = [];
            this.rebuildFilteredLinks();
            this.isLoading = false;
            this.hasSelection = false;
            this.currentSelection = null;
            this.lastSelectionKey = null;
            this.isFormVisible = false;
            this.isSubmitting = false;
            this.resetFormControls();
            this.deletingLinkIds.clear();
            this.canViewSupportingDocumentation = false;
            this.markForCheck();
            return;
        }

        if (!contextChanged) {
            this.currentSelection = normalized;
            this.hasSelection = !!normalized;
            this.evaluateAccessPermissions(normalized);
            return;
        }

        this.lastSelectionKey = reference;
        this.currentSelection = normalized;
        this.hasSelection = !!normalized;
        this.isFormVisible = false;
        this.isSubmitting = false;
        this.resetFormControls();
        this.deletingLinkIds.clear();

        if (!normalized && !this.aggregateByLicense) {
            this.links = [];
            this.rebuildFilteredLinks();
            this.isLoading = false;
            this.canViewSupportingDocumentation = false;
            this.markForCheck();
            return;
        }

        this.evaluateAccessPermissions(normalized);
    }

    private loadLinks(selection: NavigationMenuSelection | null): void {
        this.pageRequest?.unsubscribe();
        if (this.searchTimer) clearTimeout(this.searchTimer);
        const requestId = ++this.loadSequence;

        this.isLoading = true;
        this.links = [];
        this.rebuildFilteredLinks();
        this.markForCheck();

        this.pageRequest = this.questionResponseLinks
            .listPage(selection, this.getCurrentLicenseId()!, {
                page: this.pageIndex, pageSize: this.pageSize,
                search: this.linkSearchText, authors: this.selectedAuthorNames
            })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (items) => {
                    if (this.loadSequence !== requestId) {
                        return;
                    }

                    const lastPage = Math.max(1, Math.ceil(items.total / this.pageSize));
                    if (this.pageIndex > lastPage) {
                        this.pageIndex = lastPage;
                        this.loadLinks(selection);
                        return;
                    }
                    this.total = items.total;
                    this.authorOptions = items.authors;
                    this.links = items.items.map((item) => this.normalizeLink(item));
                    this.rebuildFilteredLinks();
                    this.isLoading = false;
                    this.markForCheck();
                },
                error: (error) => {
                    if (this.loadSequence !== requestId) {
                        return;
                    }

                    this.links = [];
                    this.rebuildFilteredLinks();
                    this.isLoading = false;
                    const message =
                        error?.error?.message ||
                        error?.message ||
                        "Unable to load supporting documentation links.";
                    this.notification.error("Load failed", message);
                    this.markForCheck();
                }
            });
    }

    private evaluateAccessPermissions(selection: NavigationMenuSelection | null): void {
        if (this.readOnlyUser) {
            this.canViewSupportingDocumentation = false;
            this.resetLinksState();
            this.markForCheck();
            return;
        }

        const activeLicenseId = this.getCurrentLicenseId();

        if (this.loginService.hasAdminPrivileges()) {
            this.canViewSupportingDocumentation = activeLicenseId !== null;
            if (this.canViewSupportingDocumentation) {
                this.loadLinks(selection);
            } else {
                this.resetLinksState();
            }
            this.markForCheck();
            return;
        }

        if (activeLicenseId === null) {
            this.canViewSupportingDocumentation = false;
            this.resetLinksState();
            this.markForCheck();
            return;
        }

        this.licenseAccessSubscription?.unsubscribe();
        this.licenseAccessSubscription = this.productLicensesService
            .getLicenseById(activeLicenseId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (license) => {
                    this.canViewSupportingDocumentation = this.isUserAssignedToLicense(license);
                    if (this.canViewSupportingDocumentation) {
                        this.loadLinks(selection);
                    } else {
                        this.resetLinksState();
                    }
                    this.markForCheck();
                },
                error: () => {
                    this.canViewSupportingDocumentation = false;
                    this.resetLinksState();
                    this.markForCheck();
                }
            });
    }

    private resetLinksState(): void {
        if (this.editingLinkId !== null) this.clearEditState();
        this.links = [];
        this.rebuildFilteredLinks();
        this.isLoading = false;
        this.isFormVisible = false;
        this.isSubmitting = false;
        this.deletingLinkIds.clear();
    }

    private getCurrentLicenseId(): number | null {
        return this.normalizeLicenseId(this.aggregateByLicense
            ? this.productLicenseId
            : this.productLicenseId ?? this.questionResponsesService.getActiveProductLicenseId());
    }

    private isCurrentLinkContext(licenseId: number, reference: string | null, aggregateByLicense: boolean): boolean {
        return !this.readOnlyUser && this.canViewSupportingDocumentation &&
            licenseId === this.getCurrentLicenseId() && aggregateByLicense === this.aggregateByLicense &&
            reference === this.toQuestionReference(this.currentSelection);
    }

    private resetFilters(): void {
        this.pageIndex = 1;
        this.total = 0;
        this.authorOptions = [];
        this.pageRequest?.unsubscribe();
        if (this.searchTimer) clearTimeout(this.searchTimer);
        this.areFiltersVisible = false;
        this.linkSearchText = "";
        this.selectedAuthorNames = [];
        this.rebuildFilteredLinks();
    }

    private rebuildFilteredLinks(): void {
        this.filteredLinks = this.links;
    }

    private queuePageSearch(): void {
        if (this.searchTimer) clearTimeout(this.searchTimer);
        // Cancel the previous read immediately so a slow old search cannot replace the new one.
        this.pageRequest?.unsubscribe();
        ++this.loadSequence;
        this.searchTimer = setTimeout(() => {
            this.pageIndex = 1;
            this.loadLinks(this.aggregateByLicense ? null : this.currentSelection);
        }, 250);
    }

    protected onPageChange(page: number): void {
        if (!Number.isSafeInteger(page) || page < 1 || page === this.pageIndex ||
            this.isLoading || this.isSubmitting || this.editingLinkId !== null) return;
        this.pageIndex = page;
        this.loadLinks(this.aggregateByLicense ? null : this.currentSelection);
    }

    private determineSubmissionType(): string {
        const selectedType = this.linkForm.value.type;
        if (this.isOtherType(selectedType)) {
            const customType = this.sanitizeCustomType(String(this.linkForm.value.customType ?? ""));
            return customType || SupportingDocumentationFooterComponent.DEFAULT_LINK_TYPE;
        }

        return this.normalizeLinkType(String(selectedType ?? ""));
    }

    private sanitizeCustomType(value: string): string {
        if (!value) {
            return "";
        }

        const sanitized = this.sanitizeLabel(value);
        if (!sanitized) {
            return "";
        }

        return sanitized.slice(0, SupportingDocumentationFooterComponent.CUSTOM_LINK_TYPE_MAX_LENGTH);
    }

    private updateCustomTypeState(value: unknown, options?: { markForCheck?: boolean }): void {
        const customTypeControl = this.linkForm.controls["customType"];
        if (!customTypeControl) {
            return;
        }

        const isOther = this.isOtherType(value);
        this.isCustomTypeSelected = isOther;

        if (isOther) {
            customTypeControl.setValidators([
                Validators.required,
                Validators.maxLength(SupportingDocumentationFooterComponent.CUSTOM_LINK_TYPE_MAX_LENGTH),
                this.createWhitespaceValidator()
            ]);
        } else {
            customTypeControl.reset("", { emitEvent: false });
            customTypeControl.clearValidators();
            customTypeControl.markAsPristine();
            customTypeControl.markAsUntouched();
        }

        customTypeControl.updateValueAndValidity({ emitEvent: false });

        if (options?.markForCheck ?? true) {
            this.markForCheck();
        }
    }

    private isOtherType(value: unknown): boolean {
        if (typeof value !== "string") {
            return false;
        }

        return (
            value.trim().toLowerCase() ===
            SupportingDocumentationFooterComponent.CUSTOM_LINK_TYPE.toLowerCase()
        );
    }

    private normalizeLink(dto: QuestionResponseLinkDto): SupportingDocumentationLink {
        const id = Number(dto.id) || 0;
        const userId = this.normalizeUserId(dto.userId ?? dto.user_id);
        const displayName = this.sanitizeLabel(
            dto.displayName ?? dto.display_name ?? "Supporting evidence"
        );
        const url = this.sanitizeUrl(dto.url ?? "");
        const type = this.normalizeLinkType(dto.type ?? dto.linkType ?? dto.link_type ?? "");
        const createdAt = this.normalizeDate(dto.createdAt ?? dto.created_at) ?? new Date().toISOString();
        const updatedAt = this.normalizeDate(dto.updatedAt ?? dto.updated_at);
        const questionReference = dto.questionReference ?? dto.question_reference ?? null;
        const createdByDisplayName = this.sanitizeDisplayName(
            dto.createdByDisplayName ?? dto.created_by_display_name ?? null
        );

        return { id, userId, displayName, url, type, createdAt, updatedAt, questionReference, createdByDisplayName };
    }



    protected onRequirementReferenceClick(reference: string | null | undefined): void {
        const selection = this.parseSelectionFromReference(reference);
        if (!selection) {
            return;
        }

        this.navigationDispatcher.requestNavigation(selection);
    }

    protected getRequirementReferenceLabel(reference: string | null | undefined): string {
        const selection = this.parseSelectionFromReference(reference);
        if (!selection) {
            return "";
        }

        return this.formatRequirementReferenceTarget(selection.target);
    }


    private formatRequirementReferenceTarget(target: string): string {
        const normalized = target.trim();
        if (!normalized) {
            return "";
        }

        if (normalized.toLowerCase() === "overview") {
            return "Assessment Overview";
        }

        return normalized.replace(/_/g, ".");
    }

    private parseSelectionFromReference(reference: string | null | undefined): NavigationMenuSelection | null {
        if (typeof reference !== "string") {
            return null;
        }

        const [section, target] = reference.split("|");
        if (!section || !target) {
            return null;
        }

        return this.normalizeSelection(section, target);
    }
    private normalizeSelection(sectionId: string | null | undefined, sectionKey: string | null | undefined): NavigationMenuSelection | null {
        const section = typeof sectionId === "string" ? sectionId.trim() : "";
        const target = typeof sectionKey === "string" ? sectionKey.trim() : "";

        if (!section || !target) {
            return null;
        }

        if (!SECTION_PATTERN.test(section)) {
            return null;
        }

        if (!TARGET_PATTERN.test(target)) {
            return null;
        }

        return { section, target };
    }

    private toQuestionReference(selection: NavigationMenuSelection | null): string | null {
        if (!selection) {
            return null;
        }

        return `${selection.section}|${selection.target}`;
    }

    private isUserAssignedToLicense(license: ProductLicenseSummary | null): boolean {
        const userId = this.normalizeUserId(this.loginService.getCurrentUserId());
        if (!license || userId === null) {
            return false;
        }

        const assignmentIds = [
            license.assignedIsaUserId,
            license.assignedQsaUserId,
            license.assignedQaUserId,
            ...(license.editorIds ?? [])
        ]
            .map((id) => this.normalizeUserId(id))
            .filter((id): id is number => Number.isInteger(id));

        return assignmentIds.some((id) => id === userId);
    }

    private normalizeLicenseId(value: number | null): number | null {
        const numeric = Number(value);
        return Number.isInteger(numeric) && numeric > 0 ? numeric : null;
    }

    private normalizeUserId(value: unknown): number | null {
        const numeric = Number(value);
        return Number.isInteger(numeric) && numeric > 0 ? numeric : null;
    }

    private normalizeLinkType(value: string): string {
        const sanitized = this.sanitizeLabel(value);
        if (!sanitized) {
            return SupportingDocumentationFooterComponent.DEFAULT_LINK_TYPE;
        }

        const match = this.linkTypeOptions.find(
            (option) => option.toLowerCase() === sanitized.toLowerCase()
        );

        return match ?? sanitized;
    }

    private createLinkTypeValidator(): ValidatorFn {
        return (control: AbstractControl) => {
            const value = typeof control.value === "string" ? control.value.trim() : "";
            if (!value) {
                return { required: true };
            }

            const match = this.linkTypeOptions.some(
                (option) => option.toLowerCase() === value.toLowerCase()
            );

            return match ? null : { invalidType: true };
        };
    }

    private sanitizeLabel(value: string): string {
        return value.replace(/[\u0000-\u001F\u007F]+/g, " ").replace(/\s{2,}/g, " ").trim();
    }

    private sanitizeUrl(value: string): string {
        return value.replace(/[\u0000-\u001F\u007F]+/g, "").trim();
    }

    private sanitizeDisplayName(value: string | null | undefined): string | null {
        if (!value) {
            return null;
        }

        const sanitized = String(value)
            .replace(/[\u0000-\u001F\u007F<>]+/g, " ")
            .replace(/\s{2,}/g, " ")
            .trim();

        return sanitized || null;
    }

    private normalizeDate(value: string | Date | null | undefined): string | null {
        if (!value) {
            return null;
        }

        if (value instanceof Date) {
            return Number.isNaN(value.getTime()) ? null : value.toISOString();
        }

        const trimmed = typeof value === "string" ? value.trim() : "";
        if (!trimmed) {
            return null;
        }

        const parsed = new Date(trimmed);
        return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
    }

    private createWhitespaceValidator(): ValidatorFn {
        return (control: AbstractControl) => {
            const value = typeof control.value === "string" ? control.value.trim() : "";
            if (!value) {
                return { whitespace: true };
            }
            return null;
        };
    }

    private createUrlValidator(): ValidatorFn {
        return (control: AbstractControl) => {
            const value = typeof control.value === "string" ? control.value.trim() : "";
            if (!value) {
                return null;
            }

            const normalized = this.normalizeUrlForValidation(value);
            if (!normalized) {
                return { url: true };
            }

            try {
                const parsed = new URL(normalized);
                if (!this.hasValidDomainSuffix(parsed.hostname)) {
                    return { url: true };
                }
            } catch (error) {
                return { url: true };
            }

            return null;
        };
    }

    private normalizeSubmissionUrl(value: string): string {
        return this.sanitizeUrl(value);
    }

    private normalizeUrlForValidation(value: string): string | null {
        return this.withDefaultProtocol(value);
    }

    private withDefaultProtocol(value: string | null | undefined): string | null {
        const trimmed = typeof value === "string" ? value.trim() : "";
        if (!trimmed) {
            return null;
        }

        return SupportingDocumentationFooterComponent.URL_PROTOCOL_PATTERN.test(trimmed)
            ? trimmed
            : `https://${trimmed}`;
    }

    private hasValidDomainSuffix(hostname: string): boolean {
        if (!hostname) {
            return false;
        }

        const labels = hostname.split(".");
        if (labels.length < 2) {
            return false;
        }

        if (labels.some((label) => !label.trim())) {
            return false;
        }

        const topLevelDomain = labels[labels.length - 1];
        if (!topLevelDomain || topLevelDomain.length < 2) {
            return false;
        }

        return /^[A-Za-z0-9-]+$/.test(topLevelDomain);
    }

    private markForCheck(): void {
        this.cdr.markForCheck();
    }
}
